import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQS } from "@/data/content";
import { Link } from "react-router-dom";
import Seo from "@/lib/Seo";
import ReactMarkdown from "react-markdown";

export default function FAQ() {
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };
  return (
    <div>
      <Seo
        path="/faq"
        title="FAQ — Answers to Common Business Loan Questions"
        description="Are you a lender? Will applying hurt my credit? How fast can I get funded? Straight answers to the questions we hear most."
        jsonLd={faqLd}
      />
      <section className="pt-40 pb-16 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline">FAQ · Frequently asked</div>
          <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.92]">
            Questions, <em className="italic text-[color:var(--gold)]">answered plainly</em>.
          </h1>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="text-[color:var(--bone)]/70 leading-relaxed">
              A few of the questions we hear most, on a first call. Don't see
              yours here? <Link to="/contact" className="gold-link text-[color:var(--gold)]">Ask us here</Link>.
            </p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`} className="border-b border-black/10">
                  <AccordionTrigger className="text-left font-serif text-2xl md:text-3xl leading-snug py-6 text-[color:var(--bone)] hover:text-[color:var(--gold)] transition-colors [&[data-state=open]]:text-[color:var(--gold)]">
                    <span className="flex items-baseline gap-6">
                      <span className="font-mono text-xs text-[color:var(--gold)] tracking-widest">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {f.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[color:var(--bone)]/75 leading-relaxed text-base md:text-lg pb-8 pl-14">
                    <ReactMarkdown
                      components={{
                        p: ({ children }) => <p className="[&:not(:first-child)]:mt-3">{children}</p>,
                        a: ({ href, children }) =>
                          href?.startsWith("/") ? (
                            <Link to={href} className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] transition-colors">
                              {children}
                            </Link>
                          ) : (
                            <a href={href} className="text-[color:var(--gold)] border-b border-[color:var(--gold)]/40 hover:border-[color:var(--gold)] transition-colors">
                              {children}
                            </a>
                          ),
                        strong: ({ children }) => <strong className="text-[color:var(--bone)] font-semibold">{children}</strong>,
                      }}
                    >
                      {f.a}
                    </ReactMarkdown>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
